import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import ThreeWayComparisonTable from "@/components/ui/three-way-comparison-table";
import { Sparkles, Layers, Zap, Users, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
const ComparisonPage = () => {
  const navigate = useNavigate();
  return <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-b from-blue-50 via-cyan-50/50 to-background dark:from-blue-950/30 dark:via-cyan-950/20 dark:to-background">
        <div className="container mx-auto px-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Competitive Comparison</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              See What Makes{" "}
              <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                gimmefy Different
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">Built by marketers for marketing teams who demand more than generic AI.</p>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition Cards Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[{
            icon: Layers,
            title: "One Strategic Platform",
            subtitle: "Unified, Efficient, On-Brand",
            description: "Run your entire content lifecycle — Research → Ideate → Create → Edit → Publish — in one connected system. Replace scattered tools with one brand-aligned platform.",
            gradient: "from-primary/10 to-accent/5"
          }, {
            icon: Zap,
            title: "Always-Updated AI",
            subtitle: "Secure, Flexible, Low-Cost",
            description: "Best-in-class models for copy, visuals, video, and research — always updated, compliant, and enterprise-secure. Add customisations anytime.",
            gradient: "from-emerald-500/10 to-green-500/5"
          }, {
            icon: Users,
            title: "Marketing-First Design",
            subtitle: "Fill Gaps Instantly",
            description: "Scale output without adding headcount. Workspaces mirror your org structure with brand vaults, prompt libraries, and multi-language creation.",
            gradient: "from-blue-500/10 to-cyan-500/5"
          }].map((card, index) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`p-6 rounded-2xl bg-gradient-to-br ${card.gradient} border border-border hover:shadow-lg transition-all duration-300`}>
                <div className="w-10 h-10 rounded-xl bg-background/80 flex items-center justify-center mb-4">
                  <card.icon className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">{card.title}</h3>
                <p className="text-sm font-medium text-primary mb-3">{card.subtitle}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Built for Modern Marketing Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">The Platform</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Built for Modern Marketing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to create, scale, and deliver marketing content
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: "gimmefy Studio",
                description: "The command center for marketing content",
                image: "/lovable-uploads/934ac46f-c966-480e-923a-fb538d02db36.jpg",
                href: "/studio",
                ringColor: "hover:ring-primary",
                hoverBg: "hover:bg-primary/5",
                textColor: "text-primary"
              },
              {
                title: "gimmefy Playbooks",
                description: "One-click marketing workflow stacks",
                image: "/lovable-uploads/18cd406b-3dd2-4f45-80e1-83f5e5f59739.jpg",
                href: "/playbooks",
                ringColor: "hover:ring-blue-500",
                hoverBg: "hover:bg-blue-500/5",
                textColor: "text-blue-500"
              },
              {
                title: "gimmefy Visual Lab",
                description: "AI-powered visual creation and editing",
                image: "/lovable-uploads/615b6300-1f3b-4ff6-b674-a7761e0fe271.jpg",
                href: "/visual-lab",
                ringColor: "hover:ring-emerald-500",
                hoverBg: "hover:bg-emerald-500/5",
                textColor: "text-emerald-500"
              },
              {
                title: "gimmefy Agents",
                description: "Industry-standard briefs, specialized marketing AI",
                image: "/lovable-uploads/e64282f4-5403-4f77-813c-4b5543edffc4.png",
                href: "/agents",
                ringColor: "hover:ring-amber-500",
                hoverBg: "hover:bg-amber-500/5",
                textColor: "text-amber-500"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => navigate(feature.href)}
                className={`group cursor-pointer rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ring-2 ring-transparent ${feature.ringColor} ${feature.hoverBg}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
                  <span className={`inline-flex items-center text-sm font-medium ${feature.textColor} group-hover:underline`}>
                    Learn more
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              The Full Comparison
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how gimmefy stacks up across the features that matter most to marketing teams.
            </p>
          </motion.div>
          <ThreeWayComparisonTable />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-primary/5 via-accent/5 to-background">
        <div className="container mx-auto px-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready for the Upgrade?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join marketing teams using gimmefy to create better content, faster.
            </p>
            <Button size="lg" onClick={() => navigate("/enterprise#meet-the-crew")} className="bg-gradient-to-r from-primary to-accent text-white font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300">
              See the gimmefy difference
            </Button>
          </motion.div>
        </div>
      </section>

      <FooterWithNewsletter />
    </div>;
};
export default ComparisonPage;